"use client";
import {
  Button,
  Card,
  Link,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import { Link16Regular } from "@fluentui/react-icons";
import { getDocumentIcon } from "@skanska/vsa-file-icon";
import { useTranslation } from "react-i18next";
import { useCloneToolDraft, useDoesToolHaveDraft } from "../../hooks/useTools";
import { ClientRoutes } from "../../models/ClientRoutes";
import { CreateCloneToolRequest, Tool, ToolTypes } from "../../models/Tool";
import { getToolOpenUrl, getToolType } from "../../services/ToolService";
import { VsaaColorTokens } from "../../style/VsaaColorTokens";
import { ButtonWithLoading } from "../ButtonWithLoading";
import ToolDownloadButton from "../tool/ToolDownloadButton";
import { useGetUserAccessLevel } from "../../hooks/useGetUserAccessLevel";

const useStyles = makeStyles({
  infoCard: {
    marginTop: tokens.spacingVerticalL,
  },
  header: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    fontWeight: 700,
    lineHeight: tokens.lineHeightBase300,
  },
  table: {
    width: "fit-content",
    tableLayout: "auto",
  },
  typeCell: {
    width: "auto",
    minWidth: "auto",
  },
  versionCell: {
    width: "auto",
    whiteSpace: "nowrap",
  },
  publishedDateCell: {
    width: "auto",
    whiteSpace: "nowrap",
  },
  actionsCell: {
    width: "auto",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  typeContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  icon: {
    width: "16px",
    height: "16px",
    color: VsaaColorTokens.color_text_primary,
  },
  toolLink: {
    color: VsaaColorTokens.color_hyperlink,
    overflowWrap: "break-word",
    wordBreak: "break-word",
  },
  actions: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "nowrap",
    whiteSpace: "nowrap",
  },
});

interface ActivityToolsTableProps {
  tools: Tool[];
}

interface EditToolDraftButtonProps {
  tool: Tool;
}

const formatPublishedDate = (publishedTimeInUTC: string | undefined) => {
  if (!publishedTimeInUTC) {
    return "-";
  }

  return new Date(publishedTimeInUTC).toLocaleString([], {
    hour12: false,
  });
};

const getToolTypeLabelKey = (tool: Tool) =>
  getToolType(tool) === ToolTypes.Document ? "document" : "link";

const openToolDraftInNewTab = (versionId: string) => {
  window.open(
    `${ClientRoutes.ToolsDrafts}/${versionId}`,
    "_blank",
    "noopener,noreferrer"
  );
};

const EditToolDraftButton = ({ tool }: EditToolDraftButtonProps) => {
  const { t } = useTranslation();
  const activityToolsTableKey = "activityToolsTable";
  const toolId = tool.toolId ?? "";
  const { data: draftId, isLoading: loadingHasDraft } =
    useDoesToolHaveDraft(toolId);
  const { mutateAsync: createToolDraft, isPending: createDraftPending } =
    useCloneToolDraft(toolId);

  if (loadingHasDraft) {
    return null;
  }

  const onCreateDraftClick = async () => {
    if (!tool.versionId) {
      return;
    }

    const createdDraftRequest: CreateCloneToolRequest = {
      versionId: tool.versionId,
      oldVersion: false,
    };

    const createdDraftTool = await createToolDraft(createdDraftRequest);
    openToolDraftInNewTab(createdDraftTool.versionId);
  };

  return draftId ? (
    <Button size="small" onClick={() => openToolDraftInNewTab(draftId)}>
      {t("toDraft")}
    </Button>
  ) : (
    <ButtonWithLoading
      size="small"
      onClick={onCreateDraftClick}
      isLoading={createDraftPending}
      text={t(`${activityToolsTableKey}.createDraft`)}
      disabled={!toolId}
    />
  );
};

export const ActivityToolsTable = ({ tools }: ActivityToolsTableProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const activityToolsTableKey = "activityToolsTable";
  const sortedTools = [...tools].sort((a, b) => a.title.localeCompare(b.title));
  const accessLevel = useGetUserAccessLevel();

  if (tools.length === 0) {
    return null;
  }

  return (
    <Card className={classes.infoCard}>
      <h2 className={classes.header}>
        <strong>{t(`${activityToolsTableKey}.heading`)}</strong>
      </h2>
      <Table
        aria-label={t(`${activityToolsTableKey}.heading`)}
        className={classes.table}
      >
        <TableHeader>
          <TableRow>
            <TableHeaderCell className={classes.typeCell}>
              {t("type")}
            </TableHeaderCell>
            <TableHeaderCell>{t("title")}</TableHeaderCell>
            <TableHeaderCell className={classes.versionCell}>
              {t("version")}
            </TableHeaderCell>
            <TableHeaderCell className={classes.publishedDateCell}>
              {t("publishedDate")}
            </TableHeaderCell>
            {accessLevel >= 2 && (
              <TableHeaderCell className={classes.actionsCell}>
                {t(`${activityToolsTableKey}.actions`)}
              </TableHeaderCell>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedTools.map((tool) => {
            const toolType = getToolType(tool);
            const toolUrl = getToolOpenUrl(tool);

            return (
              <TableRow key={tool.versionId}>
                <TableCell className={classes.typeCell}>
                  <span className={classes.typeContent}>
                    <Tooltip
                      content={t(getToolTypeLabelKey(tool))}
                      relationship="label"
                    >
                      {toolType === ToolTypes.Document ? (
                        <img
                          src={getDocumentIcon(tool.fileType)}
                          alt={t("documentLinkIconAltText")}
                          className={classes.icon}
                        />
                      ) : (
                        <Link16Regular className={classes.icon} />
                      )}
                    </Tooltip>
                  </span>
                </TableCell>
                <TableCell>
                  <Link
                    href={toolUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.toolLink}
                  >
                    {tool.title}
                  </Link>
                </TableCell>
                <TableCell className={classes.versionCell}>
                  {tool.version ?? "-"}
                </TableCell>
                <TableCell className={classes.publishedDateCell}>
                  {formatPublishedDate(tool.publishedTimeInUTC)}
                </TableCell>
                {accessLevel >= 2 && (
                  <TableCell className={classes.actionsCell}>
                    <div className={classes.actions}>
                      {toolType === ToolTypes.Document && (
                        <>
                          <ToolDownloadButton
                            tool={tool}
                            showLabel
                            size="small"
                          />
                          <EditToolDraftButton tool={tool} />
                        </>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
};

export default ActivityToolsTable;
